import { setup, assign, fromCallback, assertEvent } from 'xstate';
import i18next from 'i18next';
import { AIContext } from '@/apiTypes/AiTypes';
import { ChatMachineContext, ChatMachineEvent, ChatMessage } from './chatMachine.types';
import { Message } from '@/apiTypes/AiTypes';
import { streamChat } from '@/services/AiStreamService';
import { v4 } from 'uuid';

export const MAX_MESSAGE_LENGTH = 1000;

export const buildPlaceholderMap = (aiContext: AIContext): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const entry of Object.values(aiContext)) {
        if (entry.anonymousToken && entry.value != null) {
            map[entry.anonymousToken] = String(entry.value);
        }
    }
    return map;
};

export const buildAnonymizedContext = (aiContext: AIContext): AIContext => {
    const anonymized: AIContext = {};
    for (const [key, entry] of Object.entries(aiContext)) {
        if (entry.anonymousToken) {
            anonymized[key] = { ...entry, value: entry.anonymousToken };
        }
        else {
            anonymized[key] = entry;
        }
    }
    return anonymized;
};

export interface StreamMessageInput {
    message: Message;
    context: AIContext;
    conversationId: string | null;
}

const chatMachineSetup = setup({
    types: {
        context: {} as ChatMachineContext,
        events: {} as ChatMachineEvent,
    },
    actions: {
        addUserMessage: assign({
            messages: ({ context }) => {
                const trimmed = context.inputMessage.trim();
                if (!trimmed) return context.messages;
                const newMessage: ChatMessage = {
                    id: v4(),
                    role: 'user',
                    content: trimmed,
                };
                return [...context.messages, newMessage];
            },
        }),
        initStreaming: assign(({ context }) => {
            const messageId = v4();
            const streamingMessage: ChatMessage = {
                id: messageId,
                role: 'assistant',
                content: '',
                isStreaming: true,
            };
            return {
                messages: [...context.messages, streamingMessage],
                streamingMessageId: messageId,
            };
        }),
        appendStreamText: assign({
            messages: ({ context, event }) => {
                assertEvent(event, 'STREAM_TEXT_DELTA');
                return context.messages.map(msg =>
                    msg.id === context.streamingMessageId
                        ? { ...msg, content: msg.content + event.text }
                        : msg,
                );
            },
        }),
        replaceStreamText: assign({
            messages: ({ context, event }) => {
                assertEvent(event, 'STREAM_REPLACE');
                return context.messages.map(msg =>
                    msg.id === context.streamingMessageId
                        ? { ...msg, content: event.message }
                        : msg,
                );
            },
        }),
        finalizeStreamingMessage: assign({
            messages: ({ context, event }) => {
                const serverMessageId = event.type === 'STREAM_DONE' ? event.messageId : undefined;
                return context.messages.map(msg =>
                    msg.id === context.streamingMessageId
                        ? { ...msg, isStreaming: false, serverMessageId }
                        : msg,
                );
            },
            streamingMessageId: null,
        }),
        handleStreamError: assign({
            messages: ({ context, event }) => {
                assertEvent(event, 'STREAM_ERROR');
                const errorMessage = event.error.code === 'USAGE_LIMIT_EXCEEDED'
                    ? event.error.message
                    // Outside React, so the shared i18next instance rather than useTranslation.
                    : i18next.t('chat.error.generic');
                return context.messages.map(msg =>
                    msg.id === context.streamingMessageId
                        ? { ...msg, content: errorMessage, isStreaming: false }
                        : msg,
                );
            },
            currentError: ({ event }) => {
                assertEvent(event, 'STREAM_ERROR');
                return new Error(event.error.message);
            },
            streamingMessageId: null,
        }),
        clearInput: assign({
            inputMessage: '',
        }),
        setInput: assign({
            inputMessage: ({ event }) => {
                assertEvent(event, 'SET_INPUT');
                return event.value.slice(0, MAX_MESSAGE_LENGTH);
            },
        }),
        clearMessages: assign({
            messages: [],
            inputMessage: '',
            currentError: null,
            streamingMessageId: null,
            conversationId: null,
            messageFeedback: {},
        }),
        loadConversation: assign(({ event }) => {
            assertEvent(event, 'LOAD_CONVERSATION');
            return {
                messages: event.messages,
                inputMessage: '',
                currentError: null,
                streamingMessageId: null,
                conversationId: event.conversationId,
                messageFeedback: event.feedback ?? {},
            };
        }),
        setMessageFeedback: assign({
            messageFeedback: ({ context, event }) => {
                assertEvent(event, 'SET_MESSAGE_FEEDBACK');
                return {
                    ...context.messageFeedback,
                    [event.messageId]: { type: event.feedbackType, id: event.feedbackId },
                };
            },
        }),
        setConversationId: assign(({ event }) => {
            assertEvent(event, 'STREAM_METADATA');
            return {
                conversationId: event.conversationId,
            };
        }),
        mergeAiContext: assign(({ context, event }) => {
            assertEvent(event, 'MERGE_AI_CONTEXT');
            const newAiContext = { ...context.aiContext, ...event.context };

            if (event.persist) {
                const newKeys = Object.keys(event.context);
                const existingKeys = new Set(context.persistedContextKeys);
                newKeys.forEach(key => existingKeys.add(key));
                return {
                    aiContext: newAiContext,
                    placeholderMap: buildPlaceholderMap(newAiContext),
                    persistedContextKeys: Array.from(existingKeys),
                };
            }

            return { aiContext: newAiContext, placeholderMap: buildPlaceholderMap(newAiContext) };
        }),
        hydratePersistedContext: assign(({ context, event }) => {
            assertEvent(event, 'HYDRATE_PERSISTED_CONTEXT');
            // Merge persisted data FIRST so current context overwrites it
            const hydratedContext = { ...event.context, ...context.aiContext };
            const persistedKeys = Object.keys(event.context);
            const existingKeys = new Set(context.persistedContextKeys);
            persistedKeys.forEach(key => existingKeys.add(key));
            return {
                aiContext: hydratedContext,
                placeholderMap: buildPlaceholderMap(hydratedContext),
                persistedContextKeys: Array.from(existingKeys),
            };
        }),
        removeAiContextKeys: assign(({ context, event }) => {
            assertEvent(event, 'REMOVE_AI_CONTEXT_KEYS');
            const newContext = { ...context.aiContext };
            event.keys.forEach(key => delete newContext[key]);
            return {
                aiContext: newContext,
                placeholderMap: buildPlaceholderMap(newContext),
            };
        }),
        setPrePrompts: assign({
            prePrompts: ({ event }) => {
                assertEvent(event, 'SET_PRE_PROMPTS');
                return event.prompts;
            },
            prePromptsWereSet: true,
        }),
        removePrePrompts: assign({
            prePrompts: ({ context, event }) => {
                assertEvent(event, 'REMOVE_PRE_PROMPTS');
                const toRemove = new Set(event.prompts);
                return context.prePrompts.filter(p => !toRemove.has(p));
            },
            prePromptsWereSet: false,
        }),
        setInputFromPrePrompt: assign({
            inputMessage: ({ event }) => {
                assertEvent(event, 'SEND_PRE_PROMPT');
                return event.prompt;
            },
        }),
        removeUsedPrePrompt: assign({
            prePrompts: ({ context, event }) => {
                assertEvent(event, 'SEND_PRE_PROMPT');
                return context.prePrompts.filter(p => p !== event.prompt);
            },
            prePromptsWereSet: true,
        }),
    },
    actors: {
        streamMessage: fromCallback<ChatMachineEvent, StreamMessageInput>(({ sendBack, input }) => {
            const abortController = new AbortController();

            streamChat({
                message: input.message,
                context: input.context,
                conversationId: input.conversationId,
                signal: abortController.signal,
                onTextDelta: (text) => {
                    sendBack({ type: 'STREAM_TEXT_DELTA', text });
                },
                onMetadata: (metadata) => {
                    sendBack({ type: 'STREAM_METADATA', conversationId: metadata.conversationId });
                },
                onError: (error) => {
                    sendBack({ type: 'STREAM_ERROR', error });
                },
                onDone: (messageId) => {
                    sendBack({ type: 'STREAM_DONE', messageId });
                },
                onReplace: (message) => {
                    sendBack({ type: 'STREAM_REPLACE', message });
                },
            });

            return () => {
                abortController.abort();
            };
        }),
    },
    guards: {
        hasValidInput: ({ context }) => context.inputMessage.trim().length > 0,
        hasValidPrePrompt: ({ event }) => {
            assertEvent(event, 'SEND_PRE_PROMPT');
            return event.prompt.trim().length > 0;
        },
    },
});

// Base machine with placeholder actor - used with createActorContext
export const chatMachine = chatMachineSetup.createMachine({
    id: 'chat',
    initial: 'idle',
    context: {
        messages: [],
        inputMessage: '',
        currentError: null,
        streamingMessageId: null,
        aiContext: {},
        placeholderMap: {},
        prePrompts: [],
        prePromptsWereSet: false,
        conversationId: null,
        messageFeedback: {},
        persistedContextKeys: [],
    },
    on: {
        SET_INPUT: {
            actions: 'setInput',
        },
        MERGE_AI_CONTEXT: {
            actions: 'mergeAiContext',
        },
        REMOVE_AI_CONTEXT_KEYS: {
            actions: 'removeAiContextKeys',
        },
        SET_PRE_PROMPTS: {
            actions: 'setPrePrompts',
        },
        REMOVE_PRE_PROMPTS: {
            actions: 'removePrePrompts',
        },
        SET_MESSAGE_FEEDBACK: {
            actions: 'setMessageFeedback',
        },
        HYDRATE_PERSISTED_CONTEXT: {
            actions: 'hydratePersistedContext',
        },
    },
    states: {
        idle: {
            on: {
                SEND_MESSAGE: {
                    target: 'streaming',
                    guard: 'hasValidInput',
                    actions: ['addUserMessage', 'clearInput', 'initStreaming'],
                },
                CLEAR_MESSAGES: {
                    actions: 'clearMessages',
                },
                SEND_PRE_PROMPT: {
                    target: 'streaming',
                    guard: 'hasValidPrePrompt',
                    actions: ['setInputFromPrePrompt', 'addUserMessage', 'clearInput', 'removeUsedPrePrompt', 'initStreaming'],
                },
                LOAD_CONVERSATION: {
                    actions: 'loadConversation',
                },
                START_NEW_CONVERSATION: {
                    actions: 'clearMessages',
                },
            },
        },
        streaming: {
            invoke: {
                id: 'streamMessage',
                src: 'streamMessage',
                input: ({ context }) => {
                    const nonStreaming = context.messages.filter(msg => !msg.isStreaming);
                    const last = nonStreaming[nonStreaming.length - 1];
                    return {
                        message: { role: last.role, content: last.content },
                        context: buildAnonymizedContext(context.aiContext),
                        conversationId: context.conversationId,
                    };
                },
            },
            on: {
                STREAM_TEXT_DELTA: {
                    actions: 'appendStreamText',
                },
                STREAM_METADATA: {
                    actions: 'setConversationId',
                },
                STREAM_REPLACE: {
                    actions: 'replaceStreamText',
                },
                STREAM_DONE: {
                    target: 'idle',
                    actions: 'finalizeStreamingMessage',
                },
                STREAM_ERROR: {
                    target: 'idle',
                    actions: 'handleStreamError',
                },
                CANCEL_STREAM: {
                    target: 'idle',
                    actions: 'finalizeStreamingMessage',
                },
            },
        },
    },
});

export type ChatMachine = typeof chatMachine;
